import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// bcryptjs removed — password hashing must only happen server-side.
// Sending a hash from the client makes the hash itself the password (ISO 27001 A.8.5 / SOC 2 CC6.1)

const Login = () => {
  const {
    isSigningUp, setIsSigningUp,
    username, setUsername,
    password, setPassword,
    confirmPassword, setConfirmPassword,
    firstName, setFirstName,
    lastName, setLastName,
    email, setEmail,
    phone, setPhone,
    setMaskedPhone,
    setRegisteredUser,
    accessCode, setAccessCode,
    setError,
    isAdmin,setIsAdmin
  } = useContext(AuthContext);

  const navigate = useNavigate();

  const validatePassword = (password) => {
    const lengthValid = password.length >= 12 && password.length <= 16;
    const hasUppercase = /[A-Z]/.test(password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);
    return lengthValid && hasUppercase && hasSymbol && hasNumber;
  };

  const maskPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return 'XXXXXX' + digits.slice(-4);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/traderlogin`, {
        method: 'POST',
        credentials: 'include',                           // receive the HttpOnly cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!data.success) {
        toast.error(data.message || 'Invalid credentials');
        return;
      }

      // Token is in an HttpOnly cookie — never stored in localStorage.
      // Only non-sensitive display values are kept in React state via context.
      // ISO 27001 A.8.3 / SOC 2 CC6.1
      setRegisteredUser({
        username,
        loginId: data.login_id,
        isAdmin: data.is_admin,
      });

      setIsAdmin(data.is_admin);
      setPhone(data.phone);
      setMaskedPhone(maskPhone(data.phone));
      setError('');
      toast.success('Login successful!');
      navigate('/verify-phone');

    } catch (err) {
      toast.error('Server error during login');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!validatePassword(password)) {
      toast.error('Password must be 12–16 characters, include an uppercase letter, a symbol, and a number.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    try {
      // Password is sent over HTTPS — server is solely responsible for hashing.
      // Client-side hashing was removed because the hash itself becomes the credential. (ISO 27001 A.8.5)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tradersignup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          access_code: accessCode
        })
      });

      const data = await response.json();

      if (data.success) {
        setRegisteredUser({ username });
        setError('');
        setIsSigningUp(false);
        setConfirmPassword('');
        toast.success('Signup successful! Please log in.');
      } else {
        toast.error(data.error || 'Signup failed');
      }
    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Server error during signup');
    }
  };

  const toggleMode = () => {
    setIsSigningUp((prev) => !prev);
    setError('');
  };

  return (
    <form className="login-form" onSubmit={isSigningUp ? handleSignup : handleLogin}>
      <h2>{isSigningUp ? 'Sign Up' : 'Login'}</h2>

      {isSigningUp && (
        <>
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setUsername(e.target.value);
            }}
            required
          />
          <input
            type="tel"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Access Code"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            required
          />
        </>
      )}

      <input
        type="text"
        placeholder="Email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
        disabled={isSigningUp}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      {isSigningUp && (
        <input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
      )}

      <button type="submit">{isSigningUp ? 'Sign Up' : 'Login'}</button>

      <p className="toggle-link">
        {isSigningUp ? 'Already have an account?' : "Don't have an account?"}{' '}
        <span onClick={toggleMode}>{isSigningUp ? 'Log in' : 'Sign up'}</span>
      </p>

      {!isSigningUp && (
        <p className="toggle-link">
          <span onClick={() => navigate('/forgot-password')}>Forgot Password?</span>
        </p>
      )}
    </form>
  );
};

export default Login;
